import { Injectable } from "@angular/core";
import { BoxService } from "@company-box/services";
import { PricingResponse, Subcategory, Variant } from "@company-box/models";
import { formatCurrency, formatNumber } from "@angular/common";
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: "root",
})
export class CBBoxService extends BoxService {
  constructor() {
    super();
  }

  getSamplePrice(
    code: string,
    boardStrength: any,
    kraftType: string,
    width?: number,
    length?: number,
    depth?: number,
    gloss?: boolean,
    luxe?: boolean,
    twoSided?: boolean,
    options?: string,
    quantity?: number,
    rush?: boolean
  ) {
    const params = {
      params: {
        width: width??0,
        length: length??0,
        depth: depth??0,
        gloss: gloss,
        luxe: luxe,
        twoSided: twoSided,
        quantity: quantity??0,
        options: options??'',
        rush: rush,
      },
    };
    return new Promise((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/price/${code}/${boardStrength}/${kraftType}`;
      lastValueFrom(this.httpClient.post(url, params)).then(
        (response) => {
          resolve(response);
        },
        (_error) => {
          reject("Could not get price");
        }
      );
    });
  }

  listTapePrices(paperType: any, quantities: number[]) {
    const url = `${this.environment.apiHost}/box/price/tape/${paperType}/list?quantities=${quantities.join(
      ","
    )}&rush=false`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getTapePrice(paperType: any, quantity: number) {
    const url = `${this.environment.apiHost}/box/price/tape/${paperType}?quantity=${quantity}&rush=false`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override listPaddedMailerPrices(code: string, paperType: string, quantities: number[]) {
    const url = `${
      this.environment.apiHost
    }/box/price/padded-mailer/${code}/${paperType}/list?quantities=${quantities.join(
      ","
    )}&rush=false&noPrint=false`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (response: any) => {
          (response.prices as any[]).forEach((price) => {
            this.setPaddedMailerPriceLabel(price, response.paddedMailer.type.subcategory.allowCustomSample);
          });
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override getPaddedMailerPrice(code: string, paperType: string, quantity: number) {
    const url = `${this.environment.apiHost}/box/price/padded-mailer/${code}/${paperType}?quantity=${quantity}&rush=false&noPrint=false`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (response: any) => {
          this.setPaddedMailerPriceLabel(response.price, response.type.subcategory.allowCustomSample);
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override setPaddedMailerPriceLabel(price: any, allowCustomSample: boolean) {
    // console.log(`q ${price.quantity} ${price.quantity === 1 && subcategory.allowCustomSample}`);
    if (price.quantity === 1 && allowCustomSample) {
      price.label = `1 Custom Sample ${formatCurrency(price.pricePerUnit, "en-us", "$")}`;
    } else {
      price.label =
        formatNumber(price.quantity, "en-us") +
        " case(s) of " +
        price.unitsPerCase +
        " " +
        formatCurrency(price.pricePerUnit, "en-us", "$") +
        " / Mailer";
    }
  }

  override listTissuePrices(quantities: any[], weight: any) {
    const url = `${this.environment.apiHost}/box/price/tissue/White/${weight}/list?quantities=${quantities.join(
      ","
    )}&rush=false`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override getTissuePrice(quantity: any, weight: any) {
    const url = `${this.environment.apiHost}/box/price/tissue/White/${weight}?quantity=${quantity}&rush=false`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override listCrinklePrices(quantities: any[], color: any) {
    const url = `${this.environment.apiHost}/box/price/crinkle/${color}/list?quantities=${quantities.join(",")}`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override getCrinklePrice(quantity: any, color: any) {
    const url = `${this.environment.apiHost}/box/price/crinkle/${color}?quantity=${quantity}`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override listLabelPrices(quantities: any[], shape: any, paperType: any, length: any, width: any, depth: any) {
    const url = `${this.environment.apiHost}/box/price/labels/${shape}/${paperType}/list?quantities=${quantities.join(
      ","
    )}&rush=false&length=${length}&width=${width}&depth=${depth}`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override getLabelPrice(quantity: any, shape: any, paperType: any, length: any, width: any, depth: any) {
    const url = `${this.environment.apiHost}/box/price/labels/${shape}/${paperType}?quantity=${quantity}&rush=false&length=${length}&width=${width}&depth=${depth}`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override listPartitionPrices(
    boardType: any,
    depth: any,
    length: any,
    lengthCells: any,
    paperType: any,
    quantities: any[],
    width: any,
    widthCells: any,
    lengthSlots: any,
    widthSlots: any
  ): Promise<PricingResponse> {
    const url = `${
      this.environment.apiHost
    }/box/price/partitions/${boardType}/${paperType}/list?depth=${depth}&length=${length}&lengthCells=${lengthCells}&quantities=${quantities.join(
      ","
    )}&rush=false&width=${width}&widthCells=${widthCells}&lengthSlots=${lengthSlots}&widthSlots=${widthSlots}`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          const response = new PricingResponse().deserialize(res);
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override getPartitionPrice(
    boardType: any,
    depth: any,
    length: any,
    lengthCells: any,
    paperType: any,
    quantity: any,
    width: any,
    widthCells: any,
    lengthSlots: any,
    widthSlots: any
  ) {
    const url = `${this.environment.apiHost}/box/price/partitions/${boardType}/${paperType}?depth=${depth}&length=${length}&lengthCells=${lengthCells}&quantity=${quantity}&rush=false&width=${width}&widthCells=${widthCells}&lengthSlots=${lengthSlots}&widthSlots=${widthSlots}`;

    return new Promise((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  override getSubcategory(subcategoryCode: string): Promise<Subcategory> {
    return new Promise<Subcategory>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/category/subcategory/${subcategoryCode}`;
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          const subcategory = new Subcategory().deserialize(res);
          this.logger.debug("subcategory", subcategory);
          resolve(subcategory);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  override getVariants(subcategoryId: number) {
    const url = `${this.environment.apiHost}/box/category/subcategory/${subcategoryId}/list?includeSubcategory=true`;

    return new Promise<any>((resolve, reject) => {
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          const variants: any[] = [];
          (res as any[]).forEach((value) => {
            const variant = new Variant().deserialize(value);
            variants.push(variant);
          });
          resolve(variants);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }
}
